import { ReactNode } from "react";
import { useRouteMatch } from "react-router-dom";
import { useSatSteps } from "../../hooks";
import { useAppDispatch, useAppSelector } from "../../store";
import { upsertAsync } from "../../store/clientQuestionnaires";
import { Values } from "./ControlledForm";
import QuestionnaireView from "./QuestionnaireView";

type QuestionnaireScreenProps = {
  children?: ReactNode;
  validationSchema?: object;
  initialValues?: object;
  title?: string;
  serializeResponses?: (data: Values) => Record<string, string>;
  deserializeResponses?: (responses: Record<string, string>) => Values;
  overrideNextPageRoute?: (data: Values) => string | undefined | null;
  hidePrev?: boolean;
  hideNext?: boolean;
};

const QuestionnaireScreen = ({ children, deserializeResponses, hideNext, hidePrev, initialValues, overrideNextPageRoute, serializeResponses, validationSchema }: QuestionnaireScreenProps) => {
  const clientId = useRouteMatch<{ clientId: string }>().params.clientId;
  const dispatch = useAppDispatch();
  const clientQuestionnaires = useAppSelector((state) => state.clientQuestionnaires);
  const { errors, isLoading, isSubmitting, responses: savedResponses } = clientQuestionnaires;
  const { currentStep, getNextRoute, goToNextStep, goToPrevStep, pushStepHistory } = useSatSteps();
  const nextPage = (data: Values = {}) => {
    goToNextStep(overrideRoute(data));
  };

  const peekNextRoute = (data: Values = {}): string | undefined => getNextRoute(overrideRoute(data));

  const overrideRoute = (data: Values = {}) => {
    if (overrideNextPageRoute) return overrideNextPageRoute(data) ?? undefined;
  };

  const handleSave = (data: Values) => {
    const questionnaireResponses = ensureStringValues(serializeResponses ? serializeResponses(data) : data);
    const nextRoute = peekNextRoute(data);

    // Redirect to the next page immediately if nothing to save
    if (Object.keys(questionnaireResponses).length === 0 && !nextRoute) {
      nextPage(data);
      return;
    }

    const stepHistoryResponses = nextRoute ? pushStepHistory(nextRoute) : {};
    const responses = { ...questionnaireResponses, ...stepHistoryResponses };

    dispatch(upsertAsync({ responses, clientId })).then((result: any) => {
      if (!result.error) nextPage(data);
    });
  };

  let deserializedResponses;
  if (deserializeResponses && savedResponses) {
    const responses: Record<string, string> = {};

    for (const question in savedResponses) {
      if (!savedResponses.hasOwnProperty(question) || !savedResponses[question]) continue;
      responses[question] = savedResponses[question];
    }

    deserializedResponses = deserializeResponses(responses);
  }

  const _initialValues = { ...initialValues, ...deserializedResponses };
  const formProps = { validationSchema, initialValues: _initialValues, onSubmit: handleSave, key: JSON.stringify(_initialValues) };

  return (
    <QuestionnaireView
      submitFailed={Boolean(errors)}
      title={currentStep?.title}
      hidePrev={hidePrev}
      hideNext={hideNext}
      nextLabel={currentStep?.nextButtonLabel}
      onBack={goToPrevStep}
      FormProps={formProps}
      screenLocked={isLoading || isSubmitting || isLoading === undefined}
    >
      {children}
    </QuestionnaireView>
  );
};

type ConvertResponsesOptions = {
  dontKnowQuestion?: string; // question that should have "I don't know" as a response if the user checked that checkbox
};

export const serializeResponses = (fieldToQuestionMapping: Record<string, string>, opts?: ConvertResponsesOptions) => (values: Values) => {
  if (opts?.dontKnowQuestion && values.dontKnow) return { [opts?.dontKnowQuestion]: `I don't know` };
  const serializedResponses: Record<string, string> = {};

  Object.entries(values).forEach(([field, answer]) => {
    if (fieldToQuestionMapping.hasOwnProperty(field)) {
      const question = fieldToQuestionMapping[field];
      serializedResponses[question] = answer;
    }
  });
  return serializedResponses;
};

export const deserializeResponses = (fieldToQuestionMapping: Record<string, string>, opts?: ConvertResponsesOptions) => (_responses: Record<string, string>) => {
  if (opts?.dontKnowQuestion && _responses[opts?.dontKnowQuestion] === `I don't know`) return { dontKnow: true };
  const questionToFieldMapping: Record<string, string> = {};
  const formValues: Values = {};

  Object.entries(fieldToQuestionMapping).forEach(([field, question]) => {
    questionToFieldMapping[question] = field;
  });

  Object.entries(_responses).forEach(([question, answer]) => {
    if (questionToFieldMapping.hasOwnProperty(question)) {
      const field = questionToFieldMapping[question];
      formValues[field] = answer;
    }
  });
  return formValues;
};

const ensureStringValues = (values: Record<string, any>): Record<string, string> => {
  const results: Record<string, string> = {};
  Object.entries(values).forEach(([key, value]) => {
    if (value !== null && value !== undefined) results[key] = typeof value === "string" ? value : `${value}`;
  });
  return results;
};

export default QuestionnaireScreen;
